import { type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ContainerProps extends HTMLAttributes<HTMLElement> {
  size?: ContainerSize;
  /**
   * The element to render.
   *
   * Constrained to a handful of landmark-friendly tags rather than being fully
   * polymorphic: the point is to let a container carry the right semantics
   * without dragging generic element typing through every call site.
   */
  as?: 'div' | 'section' | 'main' | 'header' | 'footer' | 'article' | 'nav';
}

const SIZE_CLASSES: Record<ContainerSize, string> = {
  sm: 'max-w-2xl',
  md: 'max-w-4xl',
  lg: 'max-w-6xl',
  xl: 'max-w-7xl',
  full: 'max-w-none',
};

/**
 * Horizontal layout container.
 *
 * Centralising the gutter values here is what keeps every section optically
 * aligned on mobile; scattering `px-*` across pages is how that drifts.
 */
export function Container({
  size = 'xl',
  as: Element = 'div',
  className,
  children,
  ...rest
}: ContainerProps) {
  return (
    <Element
      className={cn('mx-auto w-full px-5 sm:px-6 lg:px-8', SIZE_CLASSES[size], className)}
      {...rest}
    >
      {children}
    </Element>
  );
}
